'use client';

import { useIntl } from 'react-intl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { usePriceFormatter } from '@packages/shared/src/hooks/usePriceFormatter/usePriceFormatter';
import type { MessageValue } from 'react-intl';
import { Content } from '../../atoms/Content/Content';
import { CallToActionButton } from '../../atoms/CallToActionButton/CallToActionButton.client';
import { TipBadge } from '../../atoms/TipBadge/TipBadge.client';

export type SideBySideWithTipBadgeProps = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * open dialog handler
   */
  openInstallmentCalculatorHandler: () => void;
};

/**
 * Promotion component side by side with tip badge
 *
 * component to promote the installment calculator in a side by side layout version with a tip badge
 *
 * */
export const SideBySideWithTipBadge = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  openInstallmentCalculatorHandler,
}: SideBySideWithTipBadgeProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormatter('INSTALLMENTCALCULATOR');

  return (
    <div className="border-color-grey bg-color-background-base relative mt-1 block w-full rounded border">
      <div className="bg-color-background-base absolute left-1/2 block -translate-x-1/2 -translate-y-1/2 px-1">
        <TipBadge />
      </div>

      <div className="flex flex-col gap-1 py-1">
        <div className="flex flex-row flex-nowrap gap-5 px-2">
          <Content
            smallText={formatMessage({
              id: 'ic.promotion.sideBySideWithTipBadge.from',
              defaultMessage: 'oder ab',
            })}
            bigText={formatPrice(singleInstallment, currency)}
          />
          <Content
            smallText={formatMessage({
              id: 'ic.promotion.sideBySideWithTipBadge.installments.prefix',
              defaultMessage: 'in',
            })}
            bigText={formatMessage<{ readonly installments: MessageValue }>(
              {
                id: 'ic.promotion.sideBySideWithTipBadge.installments',
                defaultMessage: '{installments} Raten',
              },
              {
                installments: numberOfInstallments,
              },
            )}
          />
        </div>
        <Divider />
        <CallToActionButton
          openInstallmentCalculatorHandler={openInstallmentCalculatorHandler}
          className="text-center"
        />
      </div>
    </div>
  );
};
